export const COVERAGE_STATUS = {
  COMPLETE: 'complete',
  PARTIAL_DATA: 'partial-data',
  STALE: 'stale',
  UNAVAILABLE: 'unavailable',
}

export const COVERAGE_STATUS_LABELS = {
  [COVERAGE_STATUS.COMPLETE]: 'Complete',
  [COVERAGE_STATUS.PARTIAL_DATA]: 'Partial data',
  [COVERAGE_STATUS.STALE]: 'Stale',
  [COVERAGE_STATUS.UNAVAILABLE]: 'Unavailable',
}

export const buildMovementFromProperties = (properties = []) => {
  const improved = properties.filter((row) => row.movement === 'improved').length
  const stable = properties.filter((row) => row.movement === 'stable').length
  const declined = properties.filter((row) => row.movement === 'declined').length
  const total = improved + stable + declined
  const movement = { improved, stable, declined }

  return {
    ...movement,
    improvedPercent: total ? Math.round((improved / total) * 100) : 0,
    segments: buildPortfolioBreadthSegments(movement),
  }
}

export const buildPropertyBreakdownScopeLabel = (scope, propertyCount) => {
  const countLabel = propertyCount === 1 ? 'property' : 'properties'

  if (scope?.mode === 'brand' && scope.brandName) {
    return `${propertyCount} ${scope.brandName} ${countLabel}`
  }

  if (scope?.mode === 'portfolio') {
    return `${propertyCount} Highgate ${countLabel}`
  }

  return `${propertyCount} selected ${countLabel}`
}

export const buildPortfolioBreadthSegments = (movement) => {
  if (!movement) return []

  const total = movement.improved + movement.stable + movement.declined
  if (total <= 0) return []

  return [
    { key: 'improved', label: 'Improved', count: movement.improved, tone: 'positive' },
    { key: 'stable', label: 'Stable', count: movement.stable, tone: 'neutral' },
    { key: 'declined', label: 'Declined', count: movement.declined, tone: 'negative' },
  ].map((segment) => ({
    ...segment,
    percent: Math.round((segment.count / total) * 100),
  }))
}

export const hasSignificantPropertyCountChange = (represented, baseline) => {
  if (!baseline || !represented) return false
  const delta = Math.abs(represented - baseline)
  return delta >= 10 || delta / baseline >= 0.08
}

export const getCoveragePercentage = (includedCount, totalCount) => {
  if (!totalCount) return 0
  return Math.round((includedCount / totalCount) * 100)
}

export const resolveCoverageStatus = ({
  includedPropertyCount = 0,
  totalPropertyCount = 0,
  hasChangingWeeklyCohort = false,
  isStale = false,
}) => {
  if (!totalPropertyCount || includedPropertyCount <= 0) {
    return COVERAGE_STATUS.UNAVAILABLE
  }

  if (isStale) {
    return COVERAGE_STATUS.STALE
  }

  if (hasChangingWeeklyCohort || includedPropertyCount < totalPropertyCount) {
    return COVERAGE_STATUS.PARTIAL_DATA
  }

  return COVERAGE_STATUS.COMPLETE
}

export const formatDemandIndexValue = (value) => {
  if (value == null || value === '') return '—'
  const numeric = Number(String(value).replace(/%/g, '').trim())
  if (!Number.isFinite(numeric)) return String(value).replace(/%/g, '').trim()
  return String(Math.round(numeric))
}

export const formatDemandDelta = (deltaValue, deltaDirection = 'up', deltaLabel = 'vs period start') => {
  if (deltaValue == null || Number.isNaN(Number(deltaValue))) return null
  const magnitude = Math.abs(Number(deltaValue))
  const prefix = deltaDirection === 'down' ? '-' : '+'
  return `${prefix}${magnitude} ${deltaLabel}`
}

export const formatEngagementDelta = (deltaValue, deltaDirection = 'up', deltaLabel = 'vs period start') => {
  if (deltaValue == null || Number.isNaN(Number(deltaValue))) return null
  const magnitude = Math.abs(Number(deltaValue))
  const prefix = deltaDirection === 'down' ? '-' : '+'
  return `${prefix}${magnitude} pts ${deltaLabel}`
}

export const buildCoverageVisibleCopy = ({ scope, coverage }) => {
  if (!coverage?.show) return null

  const { includedPropertyCount, totalPropertyCount, status } = coverage
  const isComplete = status === COVERAGE_STATUS.COMPLETE

  if (scope?.mode === 'property') {
    if (status === COVERAGE_STATUS.UNAVAILABLE || status === COVERAGE_STATUS.STALE) {
      return {
        variant: 'unavailable',
        text: scope.propertyName
          ? `Data unavailable for ${scope.propertyName}`
          : 'Data unavailable for this property',
      }
    }
    return null
  }

  return {
    variant: isComplete ? 'complete' : 'partial',
    countsText: `${includedPropertyCount} of ${totalPropertyCount} properties in the selected scope`,
  }
}

/** @deprecated Use buildCoverageVisibleCopy */
export const buildCoverageVisibleLine = ({ scope, coverage }) => {
  const copy = buildCoverageVisibleCopy({ scope, coverage })
  if (!copy) return null
  if (copy.variant === 'partial') return `Partial coverage · ${copy.countsText}`
  return copy.countsText || copy.text
}

export const buildCoverageInfoContent = ({ coverage, comparableCohort }) => {
  if (!coverage?.show) return ''

  const { includedPropertyCount, totalPropertyCount } = coverage
  const paragraphs = [
    `${includedPropertyCount} of ${totalPropertyCount} properties in the selected reporting scope have data for the selected period.`,
  ]

  if (comparableCohort?.show && comparableCohort.propertyCount) {
    paragraphs.push(
      `The trend compares ${comparableCohort.propertyCount} properties that have complete data across all chart intervals.`
    )
  }

  paragraphs.push('Properties without complete data are excluded and are not counted as zero.')

  return paragraphs.join('\n\n')
}

export function getExecutiveCompareMeta(comparePeriod) {
  if (comparePeriod === 'yoy') {
    return {
      show: true,
      label: 'Same period last year',
      short: 'Same period last year',
      deltaLabel: 'vs same period last year',
      deltaFactor: 1.15,
    }
  }
  if (comparePeriod === 'none') {
    return {
      show: false,
      label: '',
      short: 'None',
      deltaLabel: 'vs period start',
      deltaFactor: 0,
    }
  }
  return {
    show: true,
    label: 'Previous period',
    short: 'Previous period',
    deltaLabel: 'vs previous period',
    deltaFactor: 1,
  }
}

export function applyDemandCompareToTrend(trend = [], comparePeriod = 'mom') {
  const compareMeta = getExecutiveCompareMeta(comparePeriod)
  if (!compareMeta.show) {
    return trend.map((point) => {
      const next = { ...point }
      delete next.compareDemand
      return next
    })
  }

  const demandMultiplier = compareMeta.deltaFactor === 1.15 ? 0.85 : 0.92
  return trend.map((point) => ({
    ...point,
    compareDemand: Number.isFinite(point.demand) ? Math.round(point.demand * demandMultiplier) : undefined,
  }))
}

export const addCompareSeriesToTrend = (trend = [], compareMeta = { deltaFactor: 0 }) => {
  if (compareMeta.deltaFactor === 0 || !trend.length) return trend

  const demandMultiplier = compareMeta.deltaFactor === 1.15 ? 0.85 : 0.92
  const engagementMultiplier = compareMeta.deltaFactor === 1.15 ? 0.9 : 0.94

  return trend.map((point) => ({
    ...point,
    compareDemand: Math.round(point.demand * demandMultiplier),
    compareEngagement: Number((point.engagement * engagementMultiplier).toFixed(1)),
  }))
}

export const buildDemandEngagementCompare = (compareMeta = { deltaFactor: 0, short: '' }) =>
  compareMeta.deltaFactor === 0
    ? { show: false }
    : { show: true, label: compareMeta.short }

export const deriveDemandEngagementKpis = (trend = [], compareMeta = { deltaFactor: 1, short: '' }) => {
  const first = trend[0]
  const latest = trend[trend.length - 1]
  const hasDemandCompare =
    compareMeta.deltaFactor !== 0 && latest?.compareDemand != null
  const hasEngagementCompare =
    compareMeta.deltaFactor !== 0 && latest?.compareEngagement != null

  const demandDeltaValue = hasDemandCompare
    ? latest.demand - latest.compareDemand
    : first && latest
      ? latest.demand - first.demand
      : null
  const engagementDeltaValue = hasEngagementCompare
    ? Number((latest.engagement - latest.compareEngagement).toFixed(1))
    : first && latest
      ? Number((latest.engagement - first.engagement).toFixed(1))
      : null
  const deltaLabel =
    hasDemandCompare || hasEngagementCompare
      ? `vs ${String(compareMeta.short || 'compare period').toLowerCase()}`
      : 'vs period start'

  const demandDeltaDirection =
    compareMeta.deltaFactor === 0 || demandDeltaValue == null
      ? 'neutral'
      : demandDeltaValue >= 0
        ? 'up'
        : 'down'
  const engagementDeltaDirection =
    compareMeta.deltaFactor === 0 || engagementDeltaValue == null
      ? 'neutral'
      : engagementDeltaValue >= 0
        ? 'up'
        : 'down'

  return {
    demandIndex: {
      value: latest?.demand ?? null,
      deltaValue: compareMeta.deltaFactor === 0 ? null : demandDeltaValue,
      deltaDirection: demandDeltaDirection,
      deltaLabel,
    },
    engagementRate: {
      value: latest?.engagement != null ? `${Number(latest.engagement).toFixed(1)}%` : null,
      deltaValue: compareMeta.deltaFactor === 0 ? null : engagementDeltaValue,
      deltaDirection: engagementDeltaDirection,
      deltaLabel,
    },
  }
}

export const buildChartAccessibilitySummary = ({
  demandIndex,
  engagementRate,
  portfolioCoverage,
  comparableCohort,
  compare,
  trend = [],
  series,
}) => {
  const latest = trend[trend.length - 1]
  const parts = []

  if (series === 'engagement') {
    parts.push(`Engagement Rate ${engagementRate?.value ?? 'unavailable'}`)
  } else if (series === 'demand') {
    parts.push(`Demand Index ${demandIndex?.value ?? 'unavailable'}`)
  } else {
    parts.push(
      `Demand Index ${demandIndex?.value ?? 'unavailable'}`,
      `Engagement Rate ${engagementRate?.value ?? 'unavailable'}`
    )
  }

  if (portfolioCoverage?.show) {
    parts.push(
      `${portfolioCoverage.includedPropertyCount} of ${portfolioCoverage.totalPropertyCount} properties represented`
    )
  }

  if (comparableCohort?.propertyCount) {
    parts.push(`Comparable trend cohort ${comparableCohort.propertyCount} properties`)
  }

  if (compare?.show && compare.label) {
    parts.push(`Compare line shows ${compare.label.toLowerCase()}`)
  }

  if (latest?.label) {
    parts.push(`Latest interval ${latest.label}`)
  }

  return parts.join('. ')
}
